import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import { NavigationEnd, Router} from '@angular/router';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {BlockUI, NgBlockUI} from '../../../block-ui';
import {Store} from '@ngrx/store';
import {ToastrService} from 'ngx-toastr';
import {distinctUntilChanged, take, takeWhile, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';

import {AppSettings} from '../../../app.settings';
import {Settings} from '../../../models/settings.model';
import {MenuService} from '../menu/menu.service';
import {AuthService} from '../../../services/auth.service';
import {ProductService} from '../../../services/product.service';
import {Product} from '../../../models/product.model';
import {NotificationService} from '../../../services/notification.service';
import {ProfileService} from '../../../services/profile.service';
import {ConfigService} from '../../../services/config.service';
import {ContentService} from '../../../services/content.service';
import {Budget} from '../../../models/budget.model';
import { OrderService} from '../../../services/order.service';
import {Order} from '../../../models/order.model';
import {OrderWindow} from '../../../models/order-window.model';

import {getSiblings, isEmptyArray} from '../../../shared/utils';
import {ACTIVE_ORDER_TAB, HEADER_HEIGHT, LocalStorageService} from '../../../services/local-storage.service';
import {ECOMMERCE_TYPE, PREORDER_TYPE} from '../../../constants/order-types';
import {Pages} from '../../../enums/pages.enum';
import {PaymentService} from '../../../services/payments.service';
import {ModalService} from '../../../services/modal.service';
import {SearchService} from '../../../services/search.service';
import {MobileCheckServiceService} from '../../../services/mobile-check-service.service';
import {MultiTenantService} from '../../../services/multi-tenant.service';
import {LiveUpdateService} from '../../../services/live-update.service';
import {DistributionGroupService} from '../../../services/distribution-group-service';
import {NavItem} from '../../../models/nav-item.model';
import {ProductStatistics} from '../../../services/api-product.service';
import {PnpBudgetService} from '../../../services/pnp-budget.service';
import {selectHasUserBudgets} from '../../../store/selectors/pnp-budget.selectors';


export const HEADER_HEIGHT_WITH_CALLOUT = 179;
const HEADER_DISTRIBUTION_GROUP_HEIGHT = 25;
export const DESKTOP_HEADER_HEIGHT = 143;
export const TERMS_AND_NEWS_DELAY = 5000;

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [MenuService],
    animations: [
        trigger('showInfo', [
            state('1', style({ transform: 'rotate(180deg)' })),
            state('0', style({ transform: 'rotate(0deg)' })),
            transition('1 => 0', animate('400ms')),
            transition('0 => 1', animate('400ms'))
        ])
    ],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class HeaderComponent implements OnInit, OnDestroy {
    @BlockUI() blockUI: NgBlockUI;

    @ViewChild('stickyHeader') stickyHeader: ElementRef;
    @ViewChild('mobileStickyHeader') mobileStickyHeader: ElementRef;

    public showInfoContent = false;
    public settings: Settings;
    public menuItems: Array<NavItem>;
    public mobileNavActive = false;
    public showMobileNav = false;
    public isMobile = false
    public isTablatDevice = false
    public isMobileDevice = false
    public products: Array<Product> = [];
    // public user: User = new User({});
    public budgets: Array<Budget>;
    private dropdownTimer: ReturnType<typeof setTimeout>;
    private showMenuTimer: ReturnType<typeof setTimeout>;
    private limit = 4;
    private offset = 0;
    private alive = true;
    public unreadNotificationCount = 0;
    public activeEmployeeOrder: Order;
    public employeeOrders: Array<Order> = [];
    public featuredbrands: Array<unknown> = [];
    public orderType: string;
    public orderWindow: number;
    public activeOrderTab = 'buyingWindow';
    public activeOrderWindow: OrderWindow;

    termsContent: unknown;

    allPages = Pages;
    currentPage: Pages;

    private inited = false;

    showSearchInput = false;
    showSearchButton = true;

    ondemandMenuActive = false;
    buyingWindowMenuActive = false;

    mobileNavigationMenuVisible = false;
    mobileCartMenuVisible = false;

    headerUpdateInfoVisible = false;
    headerDistributionGroupVisible = false;
    budgetsVisible$: Observable<boolean> = of(false);

    constructor(
        // public formBuilder: FormBuilder,
        protected router: Router,
        public appSettings: AppSettings,
        protected authService: AuthService,
        public menuService: MenuService,
        protected productService: ProductService,
        private profileService: ProfileService,
        protected notificationService: NotificationService,
        public orderService: OrderService,
        private configService: ConfigService,
        private contentService: ContentService,
        protected toastr: ToastrService,
        private localStorageService: LocalStorageService,
        private paymentService: PaymentService,
        private modalService: ModalService,
        private searchService: SearchService,
        private mobileCheck: MobileCheckServiceService,
        private multitenantService: MultiTenantService,
        private updateService: LiveUpdateService,
        private distributedGroupService: DistributionGroupService,
        private pnpbudgetService: PnpBudgetService,
        private store: Store,
    ) {
        this.settings = this.appSettings.settings;
        this.menuItems = this.menuService.getHorizontalMenuItems();
    }

    ngOnInit(): void {
        this.detectPageSize();

        if (this.paymentService.paymentsMethods.length === 0) {
            this.paymentService.fetchUserPaymentMethods();
        }
        this.handleActiveOrderChange();

        this.init();        
    }

    ngOnDestroy(): void {
        this.alive = false;
    }

    // wait until order service initialized
    private init(): void {
        // get clearance and truck graphics urls
        if (this.canSeeClearance) {
            this.contentService.getClearanceUrl();
        }
        if (this.canSeeTruckGraphics) {
            this.contentService.getTruckGraphicsUrl();
        }

        this.router.events.pipe(takeWhile(() => this.alive)).subscribe(event => {
            // detect current page only if orders are loaded
            if (event instanceof NavigationEnd) {
                this.setupSearch();
                if (this.inited && this.isAuthenticated) {
                    this.setupHeader();
                }
            }

        });

        this.orderService.inited$.pipe(takeWhile(() => this.alive)).subscribe((init) => {
            if (init && !this.inited && this.isAuthenticated) {
                this.inited = true;

                this.setupHeader();

                this.blockUI.stop();

            }
        });

        this.orderService.fetchAllOrders();
        this.updateService.initializeLiveUpdater();
        this.updateService.UpdateAvailable.pipe(takeWhile(() => this.alive)).subscribe(() => {
            this.headerUpdateInfoVisible = true;
        });
        this.distributedGroupService.selectedDistributionGroup.pipe(takeWhile(() => this.alive)).subscribe( result => {
            this.headerDistributionGroupVisible = !!result;
        });

        // The budgets entry point is gated on the user actually having budgets,
        // so we kick off a budgets fetch early to populate the store. HeaderComponent
        // is mounted for the whole session and the mobile menu reads the same store
        // slice, so one fetch here covers both.
        this.pnpbudgetService.fetchPnpBudgets().pipe(take(1)).subscribe();

        this.budgetsVisible$ = this.store.select(selectHasUserBudgets).pipe(
            takeWhile(() => this.alive),
        );


        setTimeout(() => {
            this.showLatestNews();
            this.showTermsAndConditions();
        }, TERMS_AND_NEWS_DELAY);
    }

    private setupHeader(): void {
        if (this.detectCurrentPage() === true) {
            // get notifications only if page has been changed
            this.getNotifications();
        }

        // keep header height  for filters
        this.localStorageService.setItem(HEADER_HEIGHT, this.headerOffset.toString());

        this.getData();
    }

    private getNotifications(): void {
        this.notificationService.unreadCount()
            .pipe(take(1))
            .subscribe(data => {
                this.unreadNotificationCount = data;
            });
    }

    // returns true if current page has been changed
    private detectCurrentPage(): boolean {
        let result = false;
        const currentUrl: string  = this.router.url;

        let navigationPage: Pages;

        if (currentUrl.indexOf('/search-results') === 0) {
            navigationPage = Pages.Search;
        } else if ( currentUrl.indexOf('/buying-window') === 0) {
            navigationPage = Pages.BuyingWindowPage;
        } else if (currentUrl.indexOf('/on-demand') === 0) {
            navigationPage = Pages.OnDemand;
        } else if (currentUrl.indexOf('favorites') !== -1) {
            navigationPage = Pages.Favorites;
        } else if ( currentUrl === '/') {
            navigationPage = Pages.Home;
        } else {
            navigationPage = Pages.Others;
        }
        if (this.currentPage !== navigationPage) {
            result = true;
            this.currentPage = navigationPage;
            if (this.currentPage === Pages.BuyingWindowPage || this.currentPage === Pages.Home) {
                this.orderService.setCurrentOrderType(PREORDER_TYPE);
            } else if ( this.currentPage === Pages.OnDemand) {
                // set for the rest of pages ECOMMERCE_TYPE, except search result
                this.orderService.setCurrentOrderType(ECOMMERCE_TYPE);
            }
        }
    
        return result;
    }

    private getData(): void {
        // if (this.featuredbrands.length === 0) {
        //     this.configService.getConfigData('brands')
        //         .pipe(take(1))
        //         .subscribe(data => {
        //             this.featuredbrands = data;
        //         });
        // }
        //
        // this.productService.getEcomProducts(1, null, '3-0')
        //     .pipe(take(1))
        //     .subscribe(({products}) => {
        //         this.products = products;
        //     });
    }

    public onSelectModal(selection: string): void {
        this.localStorageService.setItem(ACTIVE_ORDER_TAB, selection);
    }

    public toggleMobileNav(): void {
        this.mobileNavActive = !this.mobileNavActive;
    }

    get mobileHeaderHeight(): number {
        if (!this.mobileStickyHeader) {
            return 0;
        }
        return (this.mobileStickyHeader.nativeElement.offsetHeight + 5) || 0;
    }

    get headerHeight(): number {
        if (!this.stickyHeader) {
            return 0;
        }
        return this.stickyHeader.nativeElement.offsetHeight || 0;
    }


    @HostListener('window:resize')
    public onWindowResize(): void {
        setTimeout(() => {
            this.detectPageSize();
        })
    }


    private detectPageSize(): void {
        const state = this.mobileCheck.check();
        this.isMobileDevice = state.isMobile ?? this.mobileCheck.mobileDevice;
        this.isTablatDevice = state.isTablet ?? this.mobileCheck.tabletDevice;
        this.isMobile = this.isMobileDevice || this.isTablatDevice;
        this.showMobileNav = this.isMobile;
    }   

    showMenu(type: string): void {
        if (this.authService.isRestricted) {
            return;
        }
        clearTimeout(this.dropdownTimer);
        this.showMenuTimer = setTimeout(() => {
            if (type === ECOMMERCE_TYPE) {
                this.ondemandMenuActive = true;
                this.buyingWindowMenuActive = false;
            } else {
                this.ondemandMenuActive = false;
                this.buyingWindowMenuActive = true;
            }
        }, 500)
    }

    hideMenu(type: string): void {
        clearTimeout(this.showMenuTimer);

        // check if menu already hidden
        if (type === ECOMMERCE_TYPE && this.ondemandMenuActive === false) {
            return;
        }

        if (type === PREORDER_TYPE && this.buyingWindowMenuActive === false) {
            return;
        }

        this.dropdownTimer = setTimeout(() => {
            if (type === ECOMMERCE_TYPE) {
                this.ondemandMenuActive = false;
            } else {
                this.buyingWindowMenuActive = false;
            }
        }, 200);
    }

    dropdownMouseEnter(event): void {
        clearTimeout(this.dropdownTimer);
        clearTimeout(this.showMenuTimer);
        const siblings = getSiblings(event.target);
        for (const el of siblings) {
            el.classList.remove('is-active');
        }
        event.target.classList.add('is-active');
    }

    dropdownMouseLeave(event): void {
        this.dropdownTimer = setTimeout(() => {
            event.target.classList.remove('is-active');
        }, 200);
    }

    toggleGlobalSearch(event): void {
        event.preventDefault();
        this.searchService.clearSearchResults();
        this.showSearchInput = !this.showSearchInput;

        // SearchStoreInstance.searchLabel = '';
        // SearchStoreInstance.clearSearchResults();
        // SearchStoreInstance.toggleGlobalSearch();
    }

    /**
     * it shows SAP Dialog in case of employee, otherwise order name dialog
     */
    editOrderSettings(): void {
        const order = this.orderService.activeOrder;
        this.modalService.editOrderSettings(order);
    }

    // triggers when active order window is changed
    private handleActiveOrderChange(): void {
        this.orderService.activeOrderWindow$.pipe(
            distinctUntilChanged((a: OrderWindow, b: OrderWindow) => a?.id === b?.id),
            takeWhile(() => this.alive)
        ).subscribe(orderWindow => {
            this.activeOrderWindow = orderWindow;
        })
    }

    get statistics(): ProductStatistics {
        return this.productService.statistics;
    }

    get canOder(): boolean {
        return this.authService.canOrder;
    }

    get canSeeFavorites(): boolean {
        return this.authService.canSeeFavorites && !this.authService.isRestricted;
    }

    get canSeeNotifications(): boolean {
        return this.authService.canSeeNotifications;
    }

    get headerCalloutVisible(): boolean {
        return this.currentPage === Pages.Home
            || (this.currentPage === Pages.BuyingWindowPage && this.orderService.currentOrderType === PREORDER_TYPE);
    }

    get headerOffset(): number {
        if (this.showMobileNav) {

            if (this.fixedHeader) {
                return this.mobileHeaderHeight;
            }

            return 0;
        }

        if (!this.fixedHeader) {
            return 0;
        }

        let headerHeight  =  (this.headerUpdateInfoVisible ||  this.headerCalloutVisible )
          ? HEADER_HEIGHT_WITH_CALLOUT : DESKTOP_HEADER_HEIGHT;
        if (this.headerDistributionGroupVisible) {
            headerHeight += HEADER_DISTRIBUTION_GROUP_HEIGHT;
        }
        return headerHeight;
    }


    get fixedHeader(): boolean {
        return this.headerUpdateInfoVisible
          || this.headerDistributionGroupVisible
          || this.appSettings.settings.userFeatures['fixedHeader'] === true;
    }

    get isAuthenticated(): boolean {
        return this.authService.authenticated;
    }

    get onDemandOrderItemsCount(): number {
        return this.orderService.activeODOrder ? this.orderService.activeODOrder.item_count : 0;
    }

    get buyingWindowOrderItemsCount(): number {
        return this.orderService.activeBWOrder ? this.orderService.activeBWOrder.item_count : 0;
    }

    get totalOrderItems(): string {
        const bwCount = this.showOrderWindowLabel ? this.buyingWindowOrderItemsCount : 0;
        const odCount = this.showOnDemandLabel ? this.onDemandOrderItemsCount : 0;
        if (odCount > 0 && bwCount > 0) {
            return  odCount + bwCount + '+';
        } else if (bwCount > 0) {
            return '' + bwCount;
        } else if (odCount > 0) {
            return '' + odCount;
        }
        return '';
    }

    navigateToAllBWPrograms(): false {
        this.orderService.setCurrentOrderType(PREORDER_TYPE);
        this.router.navigate([
            `/buying-window/${this.activeOrderWindow.id}/programs`
        ]);

        return false;
    }

    navigateToBWProducts(): false {
        this.orderService.setCurrentOrderType(PREORDER_TYPE);
        this.router.navigate([
            `/buying-window/${this.activeOrderWindow.id}/products`
        ]);

        return false;
    }

    navigateToODProducts(): false {
        this.orderService.setCurrentOrderType(ECOMMERCE_TYPE);
        this.onSelectModal('onDemand');
        this.router.navigate(['/on-demand/products']);

        return false;
    }

    navigateToODPrograms(): false {
        this.orderService.setCurrentOrderType(ECOMMERCE_TYPE);
        this.router.navigate(['/on-demand/programs']);

        return false;
    }

    navigateToActiveODOrder(): false {
        this.orderService.setCurrentOrderType(ECOMMERCE_TYPE);
        this.onSelectModal('onDemand');
        this.router.navigate([
            `/cart/${this.orderService.activeODOrder.id}`
        ]);

        return false;
    }

    navigateToActiveBWOrder(): false {
        this.orderService.setCurrentOrderType(PREORDER_TYPE);
        this.onSelectModal('buyingWindow');
        this.router.navigate([
            `/cart/${this.orderService.activeBWOrder.id}`
        ]);

        return false;
    }

    navigateToFavorites(event: Event) {
        event.preventDefault();
        const isBWPage = this.currentPage === Pages.BuyingWindowPage ? {bw: 1} : null;
        this.router.navigate(['/favorites'], {queryParams: isBWPage});
        return false;
    }

    onSearchInputClosed(): void {
        this.showSearchInput = false;
        if (this.currentPage === Pages.Search) {
            this.showSearchButton = true;
        }
    }

    private setupSearch(): void {
        const currentUrl: string  = this.router.url;
        if (currentUrl.indexOf('search-results') !== -1 ) {
            this.showSearchButton = false;
            this.showSearchInput = true;
        } else {
            this.showSearchInput = false;
            this.showSearchButton = true;
        }
    }


    closeMobileNav() {
        this.mobileNavActive = false;
        return true;
    }

    showPnpBudgets(): void {
        const orderId = this.orderService.activeOrder?.id;
        if (!orderId || isEmptyArray(this.orderService.activeOrder?.items)) {
            this.modalService.showPnpBudgets();
        } else {

            this.modalService.showBudgetApplyDialog(this.orderService.activeOrder?.id)
              .pipe(
                tap((result) => {
                    if (result) {
                        this.modalService.showPnpBudgets();
                    }
                }))
              .subscribe()
        }
    }

    get canSeeReports(): boolean {
        return this.authService.canSeeReports;
    }

    get canSeeClearance(): boolean {
        return this.authService.canSeeClearance;
    }

    get canSeeTruckGraphics(): boolean {
        return this.authService.canSeeTruckGraphics;
    }

    get canSeeBuyingWindows(): boolean {
        return this.authService.canBuyingWindow && this.multitenantService.showOrderWindowLabel;
    }

    get canSeeBudgets(): boolean {
        return this.authService.canSeeBudgets;
    }

    get showOnDemandLabel(): boolean {
        return this.multitenantService.showOnDemandLabel;
    }

    get showOrderWindowLabel(): boolean {
        return this.multitenantService.showOrderWindowLabel;
    }

    get onDemandType(): string {
        return ECOMMERCE_TYPE;
    }

    get buyingWindowType(): string {
        return PREORDER_TYPE;
    }

    navigateToClearance(): void {
        this.router.navigate([this.clearanceUrl]);
    }

    navigateToTruckGraphics(): void {
        this.router.navigate([this.truckGraphicsUrl]);
    }

    get clearanceUrl(): string {
        return this.contentService.clearanceUrl;
    }

    get truckGraphicsUrl(): string {
        return this.contentService.truckGraphicsUrl;
    }

    get isRestricted(): boolean {
        return this.authService.isRestricted;
    }

    get logoUrl(): string {
        return this.multitenantService.logoImageUrl;
    }

    get orderWindowLabel(): string {
        return this.multitenantService.orderWindowLabel;
    }

    get onDemandLabel(): string {
        return this.multitenantService.onDemandLabel;
    }

    get onBuyingWindowPage(): boolean {
        return this.currentPage === Pages.BuyingWindowPage && this.orderService.currentOrderType === PREORDER_TYPE
    }

    get onDemandPage(): boolean {
        return this.currentPage === Pages.OnDemand;
    }

    private showTermsAndConditions() {
        this.profileService.showTermsAndConditions(true);
    }

    private showLatestNews() {
        this.profileService.showLatestNews();
    }



}
